import type { jsPDF } from 'jspdf'

/** Method name (#1E3A5F bold) + trailing word (#414141 regular). */
const TITLE_METHOD: [number, number, number] = [30, 58, 95]
const TITLE_TRAILING: [number, number, number] = [65, 65, 65]

const RESULT_BORDER: [number, number, number] = [100, 100, 100]
const RESULT_LABEL: [number, number, number] = [68, 68, 68]
const RESULT_BORDER_W = 0.1

/** Accepted fill #63DB63 / rejected fill used across all NDT RESULT bars. */
export const NDT_RESULT_FILL = {
  accepted: [99, 219, 99] as [number, number, number],
  rejected: [235, 45, 45] as [number, number, number],
}

/**
 * Table chrome for the RESULT section — matches the ET template's taller
 * summary/result bar (padding 1.5 / minCellHeight 3) so every method
 * shapes the status strip the same way.
 */
export function getNdtResultTableOptions(margin: number) {
  return {
    theme: 'plain' as const,
    styles: {
      fontSize: 8,
      textColor: [0, 0, 0] as [number, number, number],
      cellPadding: 1.5,
      lineColor: RESULT_BORDER,
      lineWidth: RESULT_BORDER_W,
      minCellHeight: 3,
    },
    headStyles: {
      fillColor: [255, 255, 255] as [number, number, number],
      textColor: RESULT_LABEL,
      fontStyle: 'normal' as const,
      fontSize: 6.5,
      cellPadding: 1,
      lineWidth: {
        top: RESULT_BORDER_W,
        right: RESULT_BORDER_W,
        bottom: 0,
        left: RESULT_BORDER_W,
      },
      lineColor: RESULT_BORDER,
      halign: 'left' as const,
      valign: 'middle' as const,
    },
    margin: { left: margin, right: margin },
    tableLineColor: RESULT_BORDER,
    tableLineWidth: RESULT_BORDER_W,
  }
}

/** Body styles for ACCEPTED / REJECTED RESULT cells. */
export function getNdtResultBodyStyles(isRejected: boolean) {
  return {
    fontStyle: 'bold' as const,
    fontSize: 12,
    textColor: [255, 255, 255] as [number, number, number],
    halign: 'left' as const,
    fillColor: isRejected ? NDT_RESULT_FILL.rejected : NDT_RESULT_FILL.accepted,
  }
}

/**
 * Draws an NDT report title as e.g. "Eddy Current" (bold navy) + " Testing" (regular gray).
 * Optional `trailing` overrides the second word (default `"Testing"`).
 * Optional `suffix` (e.g. " - IMAGE 1") is drawn in the same gray as the trailing word.
 */
export function drawMethodTitle(
  doc: jsPDF,
  methodName: string,
  x: number,
  y: number,
  options?: { fontSize?: number; trailing?: string; suffix?: string },
): void {
  const fontSize = options?.fontSize ?? 14
  const trailing = options?.trailing ?? 'Testing'
  const suffix = options?.suffix ?? ''

  doc.setFontSize(fontSize)
  doc.setFont('helvetica', 'bold')
  doc.setTextColor(...TITLE_METHOD)
  doc.text(methodName, x, y)

  const methodWidth = doc.getTextWidth(methodName)
  doc.setFont('helvetica', 'normal')
  doc.setTextColor(...TITLE_TRAILING)
  doc.text(` ${trailing}${suffix}`, x + methodWidth, y)
}
